import { emptyFn } from "@/fp";

export const isEventOutsideRect = (el: Element) => (ev: MouseEvent) => {
  const { top, left, right, bottom } = el.getBoundingClientRect();
  const { clientX, clientY } = ev as MouseEvent;
  const INSIDE_X = clientX > left && clientX < right;
  const INSIDE_Y = clientY > top && clientY < bottom;
  return !(INSIDE_X && INSIDE_Y);
};

export const isClickOutsideRect = (el: Element) => (ev: MouseEvent) => {
  const { top, left, right, bottom } = el.getBoundingClientRect();
  const { clientX, clientY } = ev as MouseEvent;
  const INSIDE_X = clientX > left && clientX < right;
  const INSIDE_Y = clientY > top && clientY < bottom;
  return !(INSIDE_X && INSIDE_Y);
};

export const $ = (selector: string) => () => document.querySelector(selector);

export const onTouchOutside = (cb: (ev?: MouseEvent) => void) => (
  el: () => Element
): (() => void) => {
  const listener = (ev: MouseEvent) => {
    if (isClickOutsideRect(el())(ev)) {
      cb(ev);
    }
  };
  window.addEventListener("click", listener, true);
  return () => window.removeEventListener("click", listener, true);
};

export const onTouchOutsideElms = (cb: (ev?: MouseEvent) => void) => (
  els: () => Element[]
): (() => void) => {
  const listener = (ev: MouseEvent) => {
    if (els().every((el) => isClickOutsideRect(el)(ev))) {
      cb(ev);
    }
  };
  window.addEventListener("click", listener, true);
  return () => window.removeEventListener("click", listener, true);
};

export const onTouchOutsideOnce = (cb: () => void, closeAnyWay = false) => (
  el: () => Element,
  ignoreElms: () => Element[] = () => []
): (() => void) => {
  let unsubscribe = emptyFn();
  const listener = (ev: Event) => {
    unsubscribe = () => {
      window.removeEventListener("click", listener, true);
    };
    const els = ignoreElms().filter((f) => f);
    let ignore = false;
    for (const ignoreEl of els) {
      ignore = !isClickOutsideRect(ignoreEl)(ev as MouseEvent);
    }
    const theEl = el();
    if (!theEl) {
      unsubscribe();
      return;
    }
    const isOutside = isClickOutsideRect(el())(ev as MouseEvent);
    if (ignore && isOutside) {
      unsubscribe();
      // the click was inside an area that
      // should be ignored by the callback
      return;
    } else {
      //  ev.stopImmediatePropagation();
      //  ev.stopPropagation();
    }
    if (closeAnyWay) {
      cb();
      unsubscribe();
      return;
    }
    if (isOutside) {
      cb();
      unsubscribe();
    }
  };
  window.addEventListener("click", listener, true);
  return unsubscribe;
};

export const onKey = (keyStr: string, capture = true) => (
  cb: (ev: KeyboardEvent) => void
) => {
  const listener = (ev: KeyboardEvent) => {
    const { key, code } = ev;
    if (keyStr === key) {
      cb(ev);
    }
  };
  document.addEventListener("keydown", listener, capture);
  return () => document.removeEventListener("keydown", listener, capture);
};

export const onResize = (cb: (ev?: Event) => void) => {
  window.addEventListener("resize", cb, true);
  return () => window.removeEventListener("resize", cb, true);
};

export const onEvent = (eventName: string) => (
  cb: (ev: Event) => void,
  global = false
) => {
  window.addEventListener(eventName, cb, global);
  return () => window.removeEventListener(eventName, cb, global);
};

export const onElEvent = (eventName: string) => (el: () => HTMLElement) => (
  cb: (ev: Event) => void,
  global = false
) => {
  el().addEventListener(eventName, cb, global);
  return () => el().removeEventListener(eventName, cb, global);
};

export const getCssVar = (varName: string) => (el: Element): string => {
  if (varName && el) {
    return getComputedStyle(el).getPropertyValue(varName);
  } else {
    console.log("getCssVar()  el | varName are undefined");
    return "";
  }
};

/**
 * @returns 1rem in pixels.
 *
 */
export const remToPx = (rem: number): number => {
  // @ts-ignore
  const pxIn1Rem = Number.parseFloat(
    getComputedStyle(document.documentElement).fontSize as string
  );
  return pxIn1Rem * rem;
};

/**
 * @returns 1rem in pixels.
 *
 */
export const pxToRem = (px: number): number => {
  // @ts-ignore
  const pxIn1Rem = Number.parseFloat(
    getComputedStyle(document.documentElement).fontSize as string
  );
  return px / pxIn1Rem;
};
